import React from 'react';

// ============================================================================
// CHA-274 — Carbon ranked horizontal bars (chart library pattern 02)
// ============================================================================
// Reusable "GROSS BY CUSTOMER"-style chart per the Carbon chart library:
// leader bar carbon-blue-60 (#0F62FE), the rest carbon-blue-30 (#A6C8FF);
// flat fills, square corners, mono numerals. Bars grow ONCE on load
// (0.7s cubic-bezier(0.2,0.8,0.2,1), staggered 0.07s), values fade in after —
// no loops. Purely presentational: renders exactly the values it is given.
export default function CarbonBars({ title, meta, data, formatValue, maxBars = 8 }) {
  const rows = [...(data || [])]
    .filter((r) => r && r.name != null)
    .sort((a, b) => (Number(b.value) || 0) - (Number(a.value) || 0))
    .slice(0, maxBars);
  if (rows.length === 0) return null;
  const max = Math.max(1, ...rows.map((r) => Number(r.value) || 0));

  return (
    <div className="bg-carbon-white border border-carbon-border px-[22px] py-[18px] font-plex">
      <style>{`
        @keyframes carbonGrowX { from { transform: scaleX(0); } to { transform: scaleX(1); } }
        @keyframes carbonFadeIn { from { opacity: 0; } to { opacity: 1; } }
      `}</style>
      <div className="flex justify-between items-baseline mb-3.5 gap-3 flex-wrap">
        <span className="font-plex-mono text-[10.5px] tracking-[0.12em] font-semibold text-carbon-ink uppercase">
          {title}
        </span>
        {meta ? (
          <span className="font-plex-mono text-[10px] text-carbon-gray-50 uppercase">{meta}</span>
        ) : null}
      </div>
      <div className="flex flex-col gap-2">
        {rows.map((r, i) => (
          <div
            key={r.name}
            data-testid="carbon-bar-row"
            className="grid grid-cols-[150px_1fr_100px] gap-3 items-center"
          >
            <span
              className="text-[12.5px] text-carbon-gray-70 overflow-hidden text-ellipsis whitespace-nowrap"
              title={r.name}
            >
              {r.name}
            </span>
            <div className="h-4 bg-carbon-gray-10 relative">
              <div
                className={`absolute left-0 top-0 bottom-0 ${i === 0 ? 'bg-carbon-blue-60' : 'bg-carbon-blue-30'}`}
                style={{
                  width: `${Math.max(2, Math.round(((Number(r.value) || 0) / max) * 100))}%`,
                  transformOrigin: 'left',
                  animation: `carbonGrowX 0.7s ${(i * 0.07).toFixed(2)}s cubic-bezier(0.2, 0.8, 0.2, 1) both`,
                }}
              />
            </div>
            <span
              className="font-plex-mono text-xs font-semibold text-right text-carbon-ink"
              style={{ animation: `carbonFadeIn 0.4s ${(i * 0.07 + 0.5).toFixed(2)}s ease both` }}
            >
              {formatValue ? formatValue(r.value) : r.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
